import { useEffect, useState } from 'react';
import { Alert, App, Button, Form, InputNumber, Radio } from 'antd';
import { api } from '../../api';
import { ErrorNotice, FormSkeleton, PageHeader, Panel } from '../../components';
import { useLoad } from '../../lib';
import { BackToSettings } from './BackToSettings';

/**
 * Settings › Trial: how many days a new portal account may use Kivonex before it must buy a plan.
 * Stored in the website settings row (trialDays). 0 turns auto-trial off.
 */
export function TrialSettings() {
    const { message } = App.useApp();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const { data, error, loading, reload } = useLoad(() => api('/v1/admin/settings/website').then((body) => body.settings));
    const choice = Form.useWatch('choice', form);
    useEffect(() => {
        if (!data)
            return;
        const days = Number(data.trialDays ?? 7);
        form.setFieldsValue({ choice: days === 7 || days === 15 || days === 0 ? days : 'custom', days: days || 7 });
    }, [data, form]);

    async function save(values) {
        if (!data)
            return;
        const days = values.choice === 'custom' ? Number(values.days) : Number(values.choice);
        setSaving(true);
        try {
            await api('/v1/admin/settings/website', {
                method: 'PUT',
                body: JSON.stringify({ ...data, trialDays: days })
            });
            message.success(days ? `New portal accounts get a ${days}-day trial.` : 'New portal accounts no longer get a trial.');
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not save the trial length');
        }
        finally {
            setSaving(false);
        }
    }

    return (
        <>
            <PageHeader title="Trial" description="How long a new account on the portal may use Kivonex before it needs a plan." actions={<BackToSettings />} />
            <ErrorNotice error={error} />
            {loading && !data ? (
                <FormSkeleton />
            ) : (
                <Panel title="Length" description="Applies to accounts created after you save. Existing trials keep the dates they already have.">
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message="A trial locks the desktop app when it ends, even if that computer is offline. Assigning or selling a plan makes the books lifetime on that computer; only cloud services then follow the plan dates."
                    />
                    <Form form={form} layout="vertical" onFinish={save}>
                        <Form.Item name="choice" label="Trial length">
                            <Radio.Group>
                                <Radio value={7}>7 days</Radio>
                                <Radio value={15}>15 days</Radio>
                                <Radio value={0}>No trial</Radio>
                                <Radio value="custom">Other</Radio>
                            </Radio.Group>
                        </Form.Item>
                        {choice === 'custom' && (
                            <Form.Item name="days" label="Days" rules={[{ required: true, message: 'Enter the number of days.' }]}>
                                <InputNumber placeholder="7" min={1} max={365} style={{ width: 160 }} />
                            </Form.Item>
                        )}
                        <Button type="primary" htmlType="submit" loading={saving}>
                            Save
                        </Button>
                    </Form>
                </Panel>
            )}
        </>
    );
}
